import type { BounceColumnId } from "@/interfaces/reportsBounces";

/**
 * The API's sort whitelist, mirrored — MUST equal apps/gfgf-api/src/constants/reportsBounces.ts
 * `BOUNCE_SORT_KEYS`, same 4 keys, same order.
 */
export const BOUNCE_SORT_KEYS = [
  "campaignName",
  "recipientName",
  "recipientEmail",
  "recipientCompany",
] as const;
export type BounceSortKey = (typeof BOUNCE_SORT_KEYS)[number];

/** Mirrors apps/gfgf-api/src/constants/reportsBounces.ts `MAX_EMAIL_LENGTH` (RFC 5321). */
export const MAX_EMAIL_LENGTH = 254;

export type { BounceStatus } from "@/constants/campaings";
// Re-exported from the single status source of truth (`constants/campaings.ts`, `CAMPAIGN_STATUSES`)
// so the Reports → Bounces vocabulary traces back to the one constant.
export { BOUNCE_STATUSES } from "@/constants/campaings";

/** Mirrors apps/gfgf-api/src/email/constants.ts `BOUNCE_TYPES`. */
export const BOUNCE_TYPES = {
  HARD: "hard",
  SOFT: "soft",
} as const;
export type BounceType = (typeof BOUNCE_TYPES)[keyof typeof BOUNCE_TYPES];

/** Fixed column arrangement — no ColumnConfigPopover, no user preferences. */
export const COLUMN_ORDER: BounceColumnId[] = [
  "campaign",
  "recipientName",
  "recipientEmail",
  "recipientCompany",
  "bounceType",
  "status",
  "action",
];

/** CSV export columns — every column except the interactive `action` cell. */
export const CSV_COLUMN_IDS: BounceColumnId[] = [
  "campaign",
  "recipientName",
  "recipientEmail",
  "recipientCompany",
  "bounceType",
  "status",
];

/** Column id -> header label — used for both the table header and the CSV header. */
export const COLUMN_LABELS: Record<BounceColumnId, string> = {
  campaign: "Campaign",
  recipientName: "Recipient Name",
  recipientEmail: "Recipient Email",
  recipientCompany: "Recipient Company",
  bounceType: "Bounce Type",
  status: "Status",
  action: "Action",
};

/** Column id -> the API sort key it drives, or null when the column has no sort expression. */
export const COLUMN_TO_SORT_KEY: Record<BounceColumnId, BounceSortKey | null> = {
  campaign: "campaignName",
  recipientName: "recipientName",
  recipientEmail: "recipientEmail",
  recipientCompany: "recipientCompany",
  bounceType: null,
  status: null,
  action: null,
};

export const DEFAULT_SORT_COLUMN: BounceColumnId = "campaign";
export const DEFAULT_SORT_DIR = "asc";

/** Rows per page. */
export const PAGE_SIZE = 100;

/** Table minimum width before horizontal scroll. */
export const MIN_WIDTH = 980;

/** BlotterFilterBar field ids for this screen's three filters. */
export const FILTER_FIELD_IDS = {
  campaign: "campaign",
  recipientName: "recipientName",
  recipientEmail: "recipientEmail",
} as const;

/** Labels for the three filter fields above. */
export const FILTER_LABELS = {
  campaign: "Campaign",
  recipientName: "Recipient Name",
  recipientEmail: "Recipient Email",
} as const;

/** The screen's user-facing strings, in one place so a wording change is one edit. */
export const BOUNCES_TEXT = {
  searchPlaceholder: "Search and filter bounces",
  itemNoun: "bounced email", // Blotter pluralises → "22 bounced emails"
  emptyTitle: "No bounced emails — every gift reached its recipient.",
  emptyMessage: "No bounced emails match your search.",
  csvFilename: "bounced-emails.csv",
  listError: "We couldn't load bounced emails. Check your connection and try again.",
  exportError: "We couldn't export bounced emails. Check your connection and try again.",
  retry: "Retry",
  action: { fix: "Fix", ended: "Campaign ended" },
  bounceType: { hard: "Hard", soft: "Soft" } satisfies Record<BounceType, string>,
  modal: {
    title: "Fix bounced email",
    subtitleSeparator: " · ",
    bouncedLabel: "Bounced address",
    newEmailLabel: "New email address",
    cancel: "Cancel",
    resend: "Resend Gift",
    invalidEmail: "Enter a valid email address",
    tooLong: `Enter an address of at most ${MAX_EMAIL_LENGTH} characters`,
    sameEmail: "Enter a different address from the one that bounced",
    error: "We couldn't resend the gift. Check the address and try again.",
  },
  resendToast: (email: string) => `Gift resent to ${email}`,
} as const;
